// The single source for a draft contest config's field-level validity. Every
// caller that accepts a wire config (contest-api's /contest/validate-config,
// /contest/create, /contest/update) runs the untrusted value through this
// function once, so all three agree.
//
// Row 21/22 (winPoints, lossPoints) is SUR-R48/Q164: winPoints must be a
// whole number of zero or higher, and lossPoints is any whole number at all,
// negative, zero, or larger than winPoints included. That is deliberate,
// owner-declined-the-guard product policy, not an oversight; do not add a
// comparison between the two fields here.
//
// The upper bounds 400 (date range), 1000 (lives, picks, entries, revives),
// and 3650 (team reuse lockout days) are anti-abuse guards against a
// pathological request, not product rules. 400 in particular is what keeps
// the contestDayClaims write in contest-api's create transaction bounded to
// at most 400 documents.
import type { typeEndDateMode } from "../domain/contestConfig";
import {
  ADVANCE_CRITERIA,
  END_DATE_MODES,
  PICK_BASES,
  POSTPONEMENT_POLICIES,
  REVIVES_ALLOWED_MODES,
  ROUND_GROUPINGS,
  ROUND_LOCK_TIMINGS,
  SPREAD_TIMINGS,
  TIE_POLICIES,
} from "../domain/contestConfig";

// Deliberately typed as string for every enum-shaped field: this function's
// job is to decide whether an untrusted wire value IS a member of the union,
// so the input type cannot already assume that.
//
// The six optional-and-nullable fields (weekStartDay, spreadTiming,
// teamReuseLockoutDays, teamReuseMaxCount, skipsMaxPerContest,
// revivesAllowedCount) are typed with "?" here to match the wire DSL's
// "field:type?" fields exactly, which codegen renders as "field?: T | null".
// An absent key and an explicit null mean the same thing on this endpoint,
// both are "not supplied".
//
// endDate and endDateMode (ISSUE-1095): a tbd draft has no end date yet, so
// endDate is optional and nullable. An absent endDateMode means "date", which
// is what every config written before ISSUE-1095 is.
export type typeContestConfigDraft = {
  startDate: string;
  endDate?: string | null;
  endDateMode?: string | null;
  roundGrouping: string;
  weekStartDay?: number | null;
  roundLockTiming: string;
  pickBasis: string;
  spreadTiming?: string | null;
  advanceCriterion: string;
  livesAllowed: number;
  requiredPicksPerRound: number;
  maxEntriesPerMember: number;
  teamReuseAllowed: boolean;
  teamReuseLockoutDays?: number | null;
  teamReuseMaxCount?: number | null;
  skipsAllowed: boolean;
  skipsMaxPerContest?: number | null;
  revivesAllowed: string;
  revivesAllowedCount?: number | null;
  postponementPolicy: string;
  tiePolicy: string;
  tiebreaker: string;
  winPoints: number;
  lossPoints: number;
};

/**
 * A draft whose window end is known: a picked date, or for tbd the computed
 * window end (the league season's last scheduled game day). This is what a
 * service stores (ISSUE-1095).
 */
export type typeResolvedContestConfigDraft = Omit<
  typeContestConfigDraft,
  "endDate" | "endDateMode"
> & {
  endDate: string;
  endDateMode: typeEndDateMode;
};

export type typeContestConfigFieldError = {
  field: string;
  code: "VALIDATION_ERROR";
};

export type typeValidateContestConfigResult = {
  errors: typeContestConfigFieldError[];
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MAX_CONTEST_DAYS = 400;
const MAX_BOUND = 1000;
const MAX_TEAM_REUSE_LOCKOUT_DAYS = 3650;

function parseDateToUtcMs(value: string): number | null {
  if (!DATE_PATTERN.test(value)) {
    return null;
  }
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const ms = Date.UTC(year, month - 1, day);
  const parsed = new Date(ms);
  const isRealDate =
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day;
  return isRealDate ? ms : null;
}

export function countInclusiveDays({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): number {
  const startMs = parseDateToUtcMs(startDate);
  const endMs = parseDateToUtcMs(endDate);
  if (startMs === null || endMs === null) {
    return 0;
  }
  return (endMs - startMs) / 86400000 + 1;
}

export function validateContestConfig({
  config,
}: {
  config: typeContestConfigDraft;
}): typeValidateContestConfigResult {
  const errors: typeContestConfigFieldError[] = [];
  const fail = (field: string): void => {
    errors.push({ field, code: "VALIDATION_ERROR" });
  };

  // An absent key and an explicit null are the same thing on every
  // optional-and-nullable field: both mean "not supplied".
  const weekStartDay = config.weekStartDay ?? null;
  const spreadTiming = config.spreadTiming ?? null;
  const teamReuseLockoutDays = config.teamReuseLockoutDays ?? null;
  const teamReuseMaxCount = config.teamReuseMaxCount ?? null;
  const skipsMaxPerContest = config.skipsMaxPerContest ?? null;
  const revivesAllowedCount = config.revivesAllowedCount ?? null;

  // 1. startDate
  const startMs = parseDateToUtcMs(config.startDate);
  if (startMs === null) {
    fail("startDate");
  }

  // 2. endDate and endDateMode (ISSUE-1095). An absent mode means "date".
  const endDateMode = config.endDateMode ?? "date";
  if (!(END_DATE_MODES as readonly string[]).includes(endDateMode)) {
    fail("endDateMode");
  }
  const endDate = config.endDate ?? null;

  // A tbd draft with no end date has nothing to check yet: the service
  // resolves the window end from the league season. A date draft, or a tbd
  // draft that already carries a date, is checked like any end date.
  if (endDateMode === "date" || endDate !== null) {
    // 2a. endDate
    const endMs = endDate === null ? null : parseDateToUtcMs(endDate);
    if (endMs === null) {
      fail("endDate");
    }

    // 3. endDate before startDate, by plain string comparison
    if (
      startMs !== null &&
      endMs !== null &&
      endDate !== null &&
      endDate < config.startDate
    ) {
      fail("endDate");
    }

    // 4. contest range too long
    if (startMs !== null && endMs !== null && endDate !== null) {
      const dayCount = countInclusiveDays({
        startDate: config.startDate,
        endDate,
      });
      if (dayCount > MAX_CONTEST_DAYS) {
        fail("endDate");
      }
    }
  }

  // 5. roundGrouping
  const isRoundGrouping = (ROUND_GROUPINGS as readonly string[]).includes(
    config.roundGrouping,
  );
  if (!isRoundGrouping) {
    fail("roundGrouping");
  }

  // 6. weekStartDay
  if (config.roundGrouping === "week") {
    const isValidWeekStartDay =
      Number.isInteger(weekStartDay) &&
      weekStartDay !== null &&
      weekStartDay >= 1 &&
      weekStartDay <= 7;
    if (!isValidWeekStartDay) {
      fail("weekStartDay");
    }
  } else if (config.roundGrouping === "day" && weekStartDay !== null) {
    fail("weekStartDay");
  }

  // 7. roundLockTiming
  const isRoundLockTiming = (ROUND_LOCK_TIMINGS as readonly string[]).includes(
    config.roundLockTiming,
  );
  if (!isRoundLockTiming) {
    fail("roundLockTiming");
  }

  // 8. pickBasis
  const isPickBasis = (PICK_BASES as readonly string[]).includes(
    config.pickBasis,
  );
  if (!isPickBasis) {
    fail("pickBasis");
  }

  // 9. spreadTiming
  if (config.pickBasis === "spread") {
    const isSpreadTiming =
      spreadTiming !== null &&
      (SPREAD_TIMINGS as readonly string[]).includes(spreadTiming);
    if (!isSpreadTiming) {
      fail("spreadTiming");
    }
  } else if (config.pickBasis === "moneyline" && spreadTiming !== null) {
    fail("spreadTiming");
  }

  // 10. advanceCriterion
  const isAdvanceCriterion = (ADVANCE_CRITERIA as readonly string[]).includes(
    config.advanceCriterion,
  );
  if (!isAdvanceCriterion) {
    fail("advanceCriterion");
  }

  // 11. livesAllowed
  if (
    !Number.isInteger(config.livesAllowed) ||
    config.livesAllowed < 1 ||
    config.livesAllowed > MAX_BOUND
  ) {
    fail("livesAllowed");
  }

  // 12. requiredPicksPerRound
  if (
    !Number.isInteger(config.requiredPicksPerRound) ||
    config.requiredPicksPerRound < 1 ||
    config.requiredPicksPerRound > MAX_BOUND
  ) {
    fail("requiredPicksPerRound");
  }

  // 13. maxEntriesPerMember
  if (
    !Number.isInteger(config.maxEntriesPerMember) ||
    config.maxEntriesPerMember < 1 ||
    config.maxEntriesPerMember > MAX_BOUND
  ) {
    fail("maxEntriesPerMember");
  }

  // 14. teamReuseLockoutDays
  if (config.teamReuseAllowed) {
    const isValidLockoutDays =
      teamReuseLockoutDays === null ||
      (Number.isInteger(teamReuseLockoutDays) &&
        teamReuseLockoutDays >= 0 &&
        teamReuseLockoutDays <= MAX_TEAM_REUSE_LOCKOUT_DAYS);
    if (!isValidLockoutDays) {
      fail("teamReuseLockoutDays");
    }
  } else if (teamReuseLockoutDays !== null) {
    fail("teamReuseLockoutDays");
  }

  // 15. teamReuseMaxCount
  if (config.teamReuseAllowed) {
    const isValidMaxCount =
      teamReuseMaxCount === null ||
      (Number.isInteger(teamReuseMaxCount) &&
        teamReuseMaxCount >= 1 &&
        teamReuseMaxCount <= MAX_BOUND);
    if (!isValidMaxCount) {
      fail("teamReuseMaxCount");
    }
  } else if (teamReuseMaxCount !== null) {
    fail("teamReuseMaxCount");
  }

  // skipsMaxPerContest
  if (config.skipsAllowed) {
    const isValidSkipMax =
      skipsMaxPerContest === null ||
      (Number.isInteger(skipsMaxPerContest) &&
        skipsMaxPerContest >= 1 &&
        skipsMaxPerContest <= MAX_BOUND);
    if (!isValidSkipMax) {
      fail("skipsMaxPerContest");
    }
  } else if (skipsMaxPerContest !== null) {
    fail("skipsMaxPerContest");
  }

  // 16. revivesAllowed
  const isRevivesAllowed = (
    REVIVES_ALLOWED_MODES as readonly string[]
  ).includes(config.revivesAllowed);
  if (!isRevivesAllowed) {
    fail("revivesAllowed");
  }

  // 17. revivesAllowedCount
  if (config.revivesAllowed === "limited") {
    const isValidRevivesCount =
      Number.isInteger(revivesAllowedCount) &&
      revivesAllowedCount !== null &&
      revivesAllowedCount >= 1 &&
      revivesAllowedCount <= MAX_BOUND;
    if (!isValidRevivesCount) {
      fail("revivesAllowedCount");
    }
  } else if (
    config.revivesAllowed === "unlimited" &&
    revivesAllowedCount !== null
  ) {
    fail("revivesAllowedCount");
  }

  // 18. postponementPolicy
  const isPostponementPolicy = (
    POSTPONEMENT_POLICIES as readonly string[]
  ).includes(config.postponementPolicy);
  if (!isPostponementPolicy) {
    fail("postponementPolicy");
  }

  // 19. tiePolicy
  const isTiePolicy = (TIE_POLICIES as readonly string[]).includes(
    config.tiePolicy,
  );
  if (!isTiePolicy) {
    fail("tiePolicy");
  }

  // 20. tiebreaker
  const isTiebreaker = ["splitMedal", "noWinner", "commissionerPick"].includes(
    config.tiebreaker,
  );
  if (!isTiebreaker) {
    fail("tiebreaker");
  }

  // 21. winPoints. SUR-R48/Q164: a whole number, zero or higher.
  if (!Number.isInteger(config.winPoints) || config.winPoints < 0) {
    fail("winPoints");
  }

  // 22. lossPoints. SUR-R48/Q164: any whole number. Negative, zero, and
  // larger than winPoints are all valid, and never rejected here.
  if (!Number.isInteger(config.lossPoints)) {
    fail("lossPoints");
  }

  return { errors };
}
